/* eslint-disable no-nested-ternary */
import { useWeb3React } from "@web3-react/core";
import { BigNumber, ethers } from "ethers";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Swal from "sweetalert2";
import { Transaction, useTransactionStore } from "../../client/product/useTransaction";
import Button from "../../components/Button";
import WhiteModal from "../../components/Modals/WhiteModal";
import Spinner from "../../components/Spinner";
import { Body2 } from "../../components/Typography";
import {
  DECIMALS,
  MAX_UINT256,
  PAYMENT_CONTRACT_ADDRESS,
  PAYMENT_METHOD_ETH,
  TOKENS,
  TOKEN_ADDRESSES,
  TOKEN_PRECISIONS,
} from "../../constants";
import { COLOR_RED } from "../../constants/colors";
import Countries from "../../data/countries.json";
import paymentContractAbi from "../../data/paymentProcessor.json";
import { CartItem } from "../../types/Product";
import { useBalance, useContract } from "../../utils";
import erc20Abi from "../../data/erc20.json";

const PaymentButton = styled(Button)`
  display: block;
  margin-left: auto;
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

type Web3ConnectModalProps = {
  isOpen: boolean;
  handleClose?: () => void;
  data: any;
  cartItems: CartItem[];
  token: string;
};

const Error = styled.div`
  ${Body2}
  color: ${COLOR_RED};
`;

export default function CryptoPaymentModal({ isOpen, handleClose, data, cartItems, token }: Web3ConnectModalProps) {
  const { account, library } = useWeb3React();

  const transactionStore = useTransactionStore();
  const [transaction, setTransaction] = useState<Transaction>();

  const paymentContract = useContract(PAYMENT_CONTRACT_ADDRESS, paymentContractAbi, true);

  const [isLoading, setIsLoading] = useState(false);
  const balance = useBalance(token);

  const navigate = useNavigate();

  const tokenContract = useContract(TOKEN_ADDRESSES[token], erc20Abi, true);
  const [isApproved, setIsApproved] = useState(false);

  useEffect(() => {
    async function checkApproved() {
      if (token === TOKENS.ETH) {
        setIsApproved(true);
      } else if (transaction?.priceToken && tokenContract && token !== TOKENS.ETH) {
        const approved = (await tokenContract.allowance(account, PAYMENT_CONTRACT_ADDRESS)) as BigNumber;
        setIsApproved(approved.gt(ethers.utils.parseUnits(transaction.priceToken.toString(), DECIMALS[token])));
      }
    }

    checkApproved();
  }, [account, token, tokenContract, transaction, isOpen]);

  useEffect(() => {
    if (!account && handleClose) {
      handleClose();
    }
  }, [account, handleClose]);

  const hasEnoughBalance = useMemo(() => {
    if (balance && transaction) {
      return balance >= transaction.priceToken;
    }

    return false;
  }, [transaction, balance]);

  useEffect(() => {
    async function getPaymentId() {
      if (isOpen && account && transaction?.token !== TOKEN_ADDRESSES[token]) {
        setIsLoading(true);
        try {
          const countryName = Countries.find((country) => country.countryCode === data.country)!.name;
          const result = await transactionStore.mutateAsync({
            products: cartItems.map((item) => ({
              _id: item.id,
              count: item.count,
              sku: {
                option: item.option,
              },
            })),
            address: { ...data, country: countryName },
            paymentMethod: PAYMENT_METHOD_ETH,
            userId: account,
            token: TOKEN_ADDRESSES[token],
          });

          setTransaction(result.data);
        } catch (error: any) {
          const errMessage = error.response?.data?.message || error.message;

          Swal.fire({
            icon: "error",
            title: "Something went wrong",
            text: errMessage,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
          });

          console.warn(error);
          if (handleClose) {
            handleClose();
          }
        }

        setIsLoading(false);
      }
    }

    getPaymentId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token]);

  const onPay = useCallback(async () => {
    if (!paymentContract || !transaction || !library) {
      return;
    }

    setIsLoading(true);
    try {
      if (token === TOKENS.ETH) {
        const receipt = await paymentContract.pay(transaction.paymentId, {
          value: ethers.utils.parseEther(transaction.priceToken.toString()),
        });

        await library.waitForTransaction(receipt.hash);
        navigate("/transaction-success");
      } else if (isApproved && token !== TOKENS.ETH) {
        const receipt = await paymentContract.payToken(
          TOKEN_ADDRESSES[token],
          ethers.utils.parseUnits(transaction.priceToken.toString(), DECIMALS[token]),
          transaction.paymentId
        );

        await library.waitForTransaction(receipt.hash);
        navigate("/transaction-success");
      } else if (!isApproved && tokenContract && token !== TOKENS.ETH) {
        const receipt = await tokenContract.approve(PAYMENT_CONTRACT_ADDRESS, MAX_UINT256);

        await library.waitForTransaction(receipt.hash);
        setIsApproved(true);
      }
    } catch (err) {
      console.warn(err);
    }
    setIsLoading(false);
  }, [paymentContract, transaction, library, token, isApproved, tokenContract, navigate]);

  return (
    <WhiteModal title="Finish Crypto Payment" isOpen={isOpen} handleClose={handleClose}>
      <Container>
        <h3>
          {token}: {transaction?.priceToken.toFixed(TOKEN_PRECISIONS[token])}
        </h3>
        <PaymentButton variant="secondary" onClick={onPay} disabled={isLoading || !hasEnoughBalance}>
          {isLoading ? (
            <Spinner size="33px" borderWidth="0.25rem" animation="border" />
          ) : isApproved ? (
            "Pay"
          ) : (
            "Approve"
          )}
        </PaymentButton>
      </Container>
      {!hasEnoughBalance && <Error className="mt-1">You don&apos;t have enough balance</Error>}
    </WhiteModal>
  );
}
